<template>
  <!-- No BlockFieldset: the states are controlled by `readonly` and `canDecide`. -->
  <div :class="`project-${protocol.id}-verification verification-flex m-1`">
    <VerificationState
      v-for="type in protocol.verification.types"
      :key="type"
      :project="project"
      :verificationStateType="protocol.verification.stateTypeByType[type]"
      :readonly="isStateReadOnly"
      :canDecide="canDecide"
      :changeState="(verificationState) => changeState(type, verificationState)"
    />
    <VerificationState
      v-if="hasOverallState"
      :project="project"
      :verificationStateType="protocol.verification.overallStateType"
      :readonly="true"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VerificationState from '@/components/VerificationState.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`). */
const NEW_PROJECT_NUMBER = '_new';
const UNAUDITED_STATE = 'UNAUDITED';

/**
 * Block "<Protokoll>: Prüfung" of a protocol tab (see feature page projekt-protokolle): request,
 * start, approve, reject and reset of each verification of `protocol.verification`; the data
 * block of the protocol requests too. A deprecated overall state (`overallStateType`) is shown
 * read-only when set. In Legacy-Authz `VerificationState` keeps its role rules.
 */
export default {
  name: 'ProjectReportVerification',
  components: { VerificationState },
  mixins: [AccessBlockMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
    protocol: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    /** The state mutation needs a stored project number. */
    isStateReadOnly() {
      return this.isBlockReadOnly || this.project.number === NEW_PROJECT_NUMBER;
    },
    /** Matrix-Authz: the block level decides; Legacy-Authz: null keeps the role rules. */
    canDecide() {
      return this.isOrganizationRoleNavigationEnabled ? !this.isBlockReadOnly : null;
    },
    hasOverallState() {
      const { overallStateType } = this.protocol.verification;
      if (!overallStateType) {
        return false;
      }
      const overallState = this.project[overallStateType];
      return Boolean(overallState) && overallState !== UNAUDITED_STATE;
    },
  },
  methods: {
    /**
     * Dispatched by its configured name, with the protocol's name of the type argument; a
     * protocol with a single state has none and sends no type.
     */
    changeState(type, verificationState) {
      const { changeStateAction, typeArgument } = this.protocol.verification;
      return this.$store.dispatch(changeStateAction, {
        projectNumber: this.project.number,
        ...(typeArgument ? { [typeArgument]: type } : {}),
        verificationState,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.verification-flex {
  display: flex;
  gap: 1em;
  flex-flow: row wrap;
  flex-basis: 100%;
  justify-content: flex-start;
}

.verification-flex > div {
  flex: 1;
  min-width: 300px;
}
</style>
